/**
 * JeanieIQ Assist — what the TENANT sees.
 *
 * Shane, 2026-08-31: *"when we are on there tennent platform there needs to be a
 * bar on the to stating we are on... that is what we see now bit so should
 * they"*.
 *
 * The impersonation banner already existed, but only the admin doing the
 * impersonating could see it — their token carries the `imp` claim, and the
 * tenant's does not. So the person being watched was the one person on the
 * screen who could not tell. This is the other half, and it is shown to every
 * user of the tenant, not only the one who approved it.
 *
 * TWO STATES, BOTH DELIBERATE:
 *
 *   • PENDING — somebody at JeanieIQ has asked. Nothing is open. A director or
 *     admin can accept or decline; everyone else sees that it was asked, because
 *     "JeanieIQ wants into our system" is not privileged information inside a
 *     company.
 *
 *   • ACTIVE — they are in, until the stated time. Anyone senior enough can end
 *     it instantly. The countdown is shown because "access until 16:00" is a fact
 *     somebody can act on; "access granted" is not.
 *
 * POLLED, NOT PUSHED. There is no socket for this, and adding one for a banner
 * that changes a handful of times a month would be the wrong trade. Ten seconds
 * is close enough for a request to feel prompt without a request per second from
 * every user in the company — it was thirty until 2026-09-24, which read as
 * nothing happening while somebody waited on the phone.
 */
import { useState, type JSX } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCurrentAssist,
  respondToAssist,
  disconnectAssist,
  type AssistGrant,
} from '../lib/assistApi';
import { getCurrentUserRole } from '../lib/auth';
import { formatInZone, useCompanyClock } from '../lib/companyClock';

/** Director and admin, both named — roles are exact-match on this platform. */
const CAN_DECIDE = ['director', 'admin'];

function untilText(g: AssistGrant, timeZone: string): string {
  if (!g.expiresAt) return '';
  const end = new Date(g.expiresAt);
  const mins = Math.max(0, Math.round((end.getTime() - Date.now()) / 60000));
  // The company's clock, not the viewer's browser (TODO 135).
  const clock = formatInZone(timeZone, end, { hour: '2-digit', minute: '2-digit' }, 'en-GB');
  return mins > 90
    ? ` until ${clock}`
    : ` for another ${mins} minute${mins === 1 ? '' : 's'} (until ${clock})`;
}

export function AssistBanner(): JSX.Element | null {
  const qc = useQueryClient();
  const { timeZone } = useCompanyClock();
  const [hours, setHours] = useState(4);
  const role = getCurrentUserRole();
  const canDecide = role !== null && CAN_DECIDE.includes(role);

  const q = useQuery({
    queryKey: ['assist', 'current'],
    queryFn: ({ signal }) => fetchCurrentAssist(signal),
    /*
     * TEN SECONDS, down from thirty (2026-09-24). Shane: *"the access as well
     * needs to have an auto refrsh to allow instant access not having to refresh
     * the page"*.
     *
     * The moment this has to be quick is the one where somebody is on the phone
     * to us: we ask for access, and they are looking at this bar waiting for it
     * to appear. Half a minute of nothing is half a minute of "has it come
     * through yet?" — and the obvious workaround, telling them to refresh, is
     * the thing being removed.
     *
     * Ten rather than three: this runs on EVERY page for EVERY signed-in user
     * all day, where the admin queue runs on one screen somebody is watching.
     * The focus refetch below is what makes it feel instant in practice, because
     * that phone call always involves them looking back at the window.
     */
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    /*
     * The app-wide staleTime of 30_000 damps the focus refetch, and this is the
     * control that has to be right the moment somebody looks back at the window
     * after being told on the phone that we have asked. Deliberately NOT polling
     * in the background: this runs for every signed-in user on every page, where
     * the admin side runs for a handful of staff.
     */
    staleTime: 0,
    /*
     * A failure here must never take the page down. The banner is a safety
     * control on top of the app, not part of it — if the call fails the worst
     * outcome is that it is not shown, which is exactly what happens when there
     * is nothing to show anyway.
     */
    retry: false,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['assist', 'current'] });
  };

  const respondMut = useMutation({
    mutationFn: (v: { accept: boolean; level?: 'read_only' | 'full' }) =>
      respondToAssist(q.data!.id, v.accept, hours, v.level),
    onSuccess: refresh,
  });
  const disconnectMut = useMutation({
    mutationFn: () => disconnectAssist(),
    onSuccess: refresh,
  });

  const grant = q.data;
  if (!grant) return null;

  const pending = grant.status === 'pending';
  const bg = pending ? '#FEF3C7' : '#DBEAFE';
  const edge = pending ? '#F59E0B' : '#2563EB';
  const ink = pending ? '#92400E' : '#1E3A8A';

  return (
    <div
      style={{
        background: bg,
        borderBottom: `2px solid ${edge}`,
        color: ink,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        fontSize: 13,
      }}
      data-testid="assist-banner"
    >
      <strong style={{ fontSize: 13 }}>
        {pending ? 'JeanieIQ has asked for access' : 'JeanieIQ Assist is connected'}
      </strong>

      <span style={{ opacity: 0.9 }}>
        {grant.requestedByEmail}
        {pending ? '' : untilText(grant, timeZone)}
        {!pending && (
          <>
            {' — '}
            <strong>
              {grant.accessLevel === 'full' ? 'can make changes' : 'look only'}
            </strong>
          </>
        )}
        {' — '}
        {/* Their words, shown as written. This is what they are being judged on. */}
        <em>{grant.reason}</em>
      </span>

      <span style={{ flex: 1 }} />

      {pending && canDecide && (
        <>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            for
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              style={{ padding: '3px 6px', borderRadius: 4, border: `1px solid ${edge}` }}
              data-testid="assist-hours"
            >
              <option value={1}>1 hour</option>
              <option value={4}>4 hours</option>
              <option value={8}>8 hours</option>
              <option value={24}>24 hours</option>
            </select>
          </label>
          {/*
            * TWO ANSWERS, NOT ONE, and look-only comes first. Shane, 2026-08-31:
            * *"look only as default then the fix"*. The grant decides whether we
            * are in; this decides what we can touch, and it is THEIR call — a
            * display glitch does not need the same access as a broken payroll run.
            */}
          <button
            type="button"
            onClick={() => respondMut.mutate({ accept: true, level: 'read_only' })}
            disabled={respondMut.isPending}
            data-testid="assist-accept"
            title="They can look at anything this account can see, but cannot change anything"
            style={{
              background: 'var(--navy)', color: 'white', border: 'none',
              padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Allow — look only
          </button>
          <button
            type="button"
            onClick={() => respondMut.mutate({ accept: true, level: 'full' })}
            disabled={respondMut.isPending}
            data-testid="assist-accept-full"
            title="They can also make changes, using this account's permissions"
            style={{
              background: 'transparent', color: ink, border: `1px solid ${edge}`,
              padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Allow — look and fix
          </button>
          <button
            type="button"
            onClick={() => respondMut.mutate({ accept: false })}
            disabled={respondMut.isPending}
            data-testid="assist-decline"
            style={{
              background: 'transparent', color: ink, border: `1px solid ${edge}`,
              padding: '6px 14px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Decline
          </button>
        </>
      )}

      {pending && !canDecide && (
        <span style={{ opacity: 0.85 }}>A director or admin can allow or decline this.</span>
      )}

      {!pending && canDecide && (
        <button
          type="button"
          onClick={() => disconnectMut.mutate()}
          disabled={disconnectMut.isPending}
          data-testid="assist-disconnect"
          style={{
            background: 'var(--red)', color: 'var(--black)', border: 'none',
            padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {disconnectMut.isPending ? 'Disconnecting…' : 'Disconnect'}
        </button>
      )}
    </div>
  );
}
